import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("crs_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem("crs_token") || null);
  const [loading, setLoading] = useState(false);

  const persist = useCallback((t, u) => {
    if (t) localStorage.setItem("crs_token", t);
    if (u) localStorage.setItem("crs_user", JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persist(data.token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const signup = useCallback(async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", payload);
      persist(data.token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [persist]);

  const googleSignIn = useCallback(async (access_token) => {
    const { data } = await api.post("/auth/google", { access_token });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const refreshMe = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await api.get("/auth/me");
      persist(token, data);
    } catch {
      logout();
    }
  }, [token, persist]);

  const updateMe = useCallback(async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    persist(token, data);
    return data;
  }, [token, persist]);

  const logout = useCallback(() => {
    localStorage.removeItem("crs_token");
    localStorage.removeItem("crs_user");
    setUser(null);
    setToken(null);
  }, []);

  useEffect(() => {
    if (token && !user) refreshMe();
  }, [token, user, refreshMe]);

  return (
    <AuthCtx.Provider value={{ user, token, loading, login, signup, googleSignIn, logout, refreshMe, updateMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
